import { Link, useLocation, useSearchParams } from 'react-router'
import { useState, useRef, type FormEvent } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import Loader from '@components/Loader/Loader'
import useAuthSignup, { SignupData } from '@hooks/useSignup'
import ReCAPTCHA from 'react-google-recaptcha'
import { RECAPTCHA_ID } from 'src/constants/global'
import { getPlanFromPath, getPricingPath, isPlanId } from 'src/constants/plans'

import loginVideoWebm from '@static/videos/login.webm'
import loginVideoPoster from '@static/images/login/login-bg.webp'
import logo from '@static/vectors/logo-datalab.svg'
import './Signup.css'

type SignupState = 'idle' | 'loading' | 'success' | 'error'

const PHONE_PATTERN = '[+]?[\\d\\s\\(\\)\\-]{7,}'
const TOTAL_STEPS = 4
const CONDITIONS_POLICY_URL =
  'https://legal.adigital.org/datalab/modalidades/invitado'
const PRIVACY_POLICY_URL = 'https://datalab.adigital.org/privacy'

export default function Signup() {
  const intl = useIntl()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { signup } = useAuthSignup()
  const [state, setState] = useState<SignupState>('idle')
  const [step, setStep] = useState(0)
  const formRef = useRef<HTMLFormElement>(null)
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null)
  const recaptchaRef = useRef<ReCAPTCHA>(null)

  const navState = location.state as { from?: string; email?: string } | null

  const prefilledEmail = navState?.email

  const planParam = searchParams.get('plan')
  let from =
    navState?.from ??
    (isPlanId(planParam) ? getPricingPath(planParam) : undefined)
  if (from?.startsWith('/')) from = from.substring(1)

  const requestedPlan = getPlanFromPath(from)

  const isLastStep = step === TOTAL_STEPS - 1
  const canSubmit = Boolean(recaptchaToken)

  const validateCurrentStep = () => {
    const form = formRef.current
    if (!form) return false
    const activeStep = form.querySelector('.signup-step.active')
    if (!activeStep) return false
    const inputs = activeStep.querySelectorAll('input')
    for (const input of inputs) {
      if (!input.reportValidity()) return false
    }
    return true
  }

  const handleNext = () => {
    if (validateCurrentStep()) setStep((s) => Math.min(s + 1, TOTAL_STEPS - 1))
  }

  const handleBack = () => {
    setStep((s) => Math.max(s - 1, 0))
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!canSubmit || !recaptchaToken) return

    const formData = new FormData(e.currentTarget)
    const data: SignupData = {
      name: String(formData.get('name')),
      surname: String(formData.get('surname')),
      email: String(formData.get('email')),
      phone: String(formData.get('phone')),
      company: String(formData.get('company')),
      role: String(formData.get('role')),
      recaptchaToken,
      from
    }

    try {
      setState('loading')
      const res = await signup(data)
      if (res.ok) {
        setState('success')
      } else {
        setState('error')
        recaptchaRef.current?.reset()
        setRecaptchaToken(null)
      }
    } catch (error) {
      console.warn('Signup error:', error)
      setState('error')
      recaptchaRef.current?.reset()
      setRecaptchaToken(null)
    }
  }

  return (
    <div className="page" id="signup">
      <div className="split">
        <div className="item item-left">
          <video autoPlay muted loop playsInline poster={loginVideoPoster}>
            <source src={loginVideoWebm} type="video/webm" />
            <img
              src={loginVideoPoster}
              alt="login animation fallback"
              aria-hidden="true"
            />
          </video>
        </div>
        <div className="item item-right">
          <form id="signup-card" ref={formRef} onSubmit={handleSubmit}>
            <img src={logo} alt="datalab logo" />
            <h1>
              <FormattedMessage id="signup.title" />
            </h1>

            {state === 'loading' && (
              <div className="signup-loading">
                <Loader />
              </div>
            )}

            {state === 'success' && (
              <div className="signup-mail-success">
                <span>
                  <FormattedMessage id="signup.success" />
                </span>
              </div>
            )}

            {(state === 'idle' || state === 'error') && (
              <>
                {requestedPlan && (
                  <p className="signup-plan-context">
                    <FormattedMessage
                      id="signup.plan-context"
                      values={{
                        plan: (
                          <strong key="plan">
                            <FormattedMessage
                              id={`pricing.plans.${requestedPlan}.name`}
                            />
                          </strong>
                        )
                      }}
                    />
                  </p>
                )}

                <div className="signup-steps">
                  <div className={`signup-step${step === 0 ? ' active' : ''}`}>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.name" />
                      </strong>
                      <input required type="text" name="name" />
                    </label>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.surname" />
                      </strong>
                      <input required type="text" name="surname" />
                    </label>
                  </div>

                  <div className={`signup-step${step === 1 ? ' active' : ''}`}>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.email" />
                      </strong>
                      <input
                        required
                        type="email"
                        name="email"
                        placeholder="email@example.com"
                        defaultValue={prefilledEmail}
                      />
                    </label>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.phone" />
                      </strong>
                      <input
                        required
                        type="tel"
                        name="phone"
                        pattern={PHONE_PATTERN}
                        title={intl.formatMessage({ id: 'signup.phone.hint' })}
                      />
                    </label>
                  </div>

                  <div className={`signup-step${step === 2 ? ' active' : ''}`}>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.company" />
                      </strong>
                      <input required type="text" name="company" />
                    </label>
                    <label>
                      <strong>
                        <FormattedMessage id="signup.label.role" />
                      </strong>
                      <input required type="text" name="role" />
                    </label>
                  </div>

                  <div
                    className={`signup-step signup-step-captcha${step === 3 ? ' active' : ''}`}
                  >
                    <div className="signup-recaptcha">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={RECAPTCHA_ID}
                        onChange={(token) => setRecaptchaToken(token)}
                        onExpired={() => setRecaptchaToken(null)}
                      />
                    </div>
                    <div className="signup-privacy">
                      <p>
                        <FormattedMessage
                          id="signup.conditions.text"
                          values={{
                            link: (
                              <a
                                key="conditions-link"
                                href={CONDITIONS_POLICY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FormattedMessage id="signup.label.privacy.linkConditionsText" />
                              </a>
                            )
                          }}
                        />
                      </p>
                      <p>
                        <FormattedMessage
                          id="signup.privacy.text"
                          values={{
                            link: (
                              <a
                                key="privacy-link"
                                href={PRIVACY_POLICY_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <FormattedMessage id="signup.label.privacy.linkPrivacyText" />
                              </a>
                            )
                          }}
                        />
                      </p>
                    </div>
                  </div>
                </div>

                {state === 'error' && (
                  <div className="error-message" role="alert">
                    <FormattedMessage id="signup.error.generic" />
                  </div>
                )}

                <div className="signup-progress">
                  {Array.from({ length: TOTAL_STEPS }, (_, i) => (
                    <span
                      key={i}
                      className={`step-segment${i <= step ? ' active' : ''}`}
                    />
                  ))}
                </div>

                <div className="signup-actions">
                  {step > 0 && (
                    <button
                      type="button"
                      className="standart-button"
                      onClick={handleBack}
                    >
                      <span className="icon icon-angle-left" />
                      <span>
                        <FormattedMessage id="signup.back" />
                      </span>
                    </button>
                  )}

                  {isLastStep ? (
                    <button
                      type="submit"
                      className="cta-button submit-button"
                      disabled={!canSubmit}
                    >
                      <span>
                        <FormattedMessage id="signup.submit" />
                      </span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="cta-button continue-button"
                      onClick={handleNext}
                    >
                      <span>
                        <FormattedMessage id="signup.continue" />
                      </span>
                      <span className="icon icon-angle-right" />
                    </button>
                  )}
                </div>
              </>
            )}
            <p className="text-login">
              <FormattedMessage
                id="signup.text"
                values={{
                  link: (
                    <Link
                      key="signup-login"
                      to="/login"
                      state={{ ...(navState ?? {}), from }}
                    >
                      <FormattedMessage id="signup.login" />
                    </Link>
                  )
                }}
              />
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
